import { errMsg } from "./api";
import { tx, offlineNow, isNetErr, cachePut } from "./idb";

export const QUEUE_EVENT = "c369-queue", SYNC_NOW = "c369-sync-now", SYNCED = "c369-synced", CLASH_EVENT = "c369-clash";
const ping = (e = QUEUE_EVENT) => window.dispatchEvent(new Event(e));

export const listQueue = async () => ((await tx("queue", "readonly", (s) => s.getAll())) || []).sort((a, b) => a.created_at.localeCompare(b.created_at));
const put = (item) => tx("queue", "readwrite", (s) => s.put(item));
export const fixQueued = async (it, aadhaar) => { await put({ ...it, error: null, data: { ...it.data, body: { ...it.data.body, aadhaar_number: aadhaar } } }); ping(); if (!offlineNow()) ping(SYNC_NOW); };
export const removeQueued = async (id) => { await tx("queue", "readwrite", (s) => s.delete(id)); ping(); };
// Sign-out on a shared phone: drop anything not yet uploaded and any clash notes.
export const clearQueue = async () => { await tx("queue", "readwrite", (s) => s.clear()); [CLASH_KEY, "c369-prewarm", VISIT, SINCE].forEach((k) => localStorage.removeItem(k)); ping(); };
export const enqueue = async (kind, label, data) => {
  await put({ id: crypto.randomUUID(), kind, label, data, created_at: new Date().toISOString(), error: null });
  if (offlineNow()) {
    const v = getVisit() || { started_at: localStorage.getItem(SINCE) || new Date().toISOString(), items: [] };
    setVisit({ ...v, items: [...v.items, { label, kind, saved_at: new Date().toISOString() }] });
  }
  ping(); if (!offlineNow()) ping(SYNC_NOW);
};

// Site visit = from losing signal until everything saved on the phone has uploaded.
const VISIT = "c369-visit", SINCE = "c369-offline-since";
export const getVisit = () => JSON.parse(localStorage.getItem(VISIT) || "null");
const setVisit = (v) => (v ? localStorage.setItem(VISIT, JSON.stringify(v)) : localStorage.removeItem(VISIT));
export const markOffline = () => { if (!localStorage.getItem(SINCE)) localStorage.setItem(SINCE, new Date().toISOString()); };
export const markOnline = () => {
  localStorage.removeItem(SINCE);
  const v = getVisit();
  if (v && !v.back_online_at) setVisit({ ...v, back_online_at: new Date().toISOString() });
};
const closeVisit = async (api) => {
  const v = getVisit();
  if (!v) return;
  const left = await listQueue();
  if (left.some((i) => !i.error)) return;
  try { await api.post("/site-visits", { ...v, back_online_at: v.back_online_at || new Date().toISOString(), failed: left.length }); setVisit(null); }
  catch (e) { if (e.response?.status === 403 || e.response?.status === 422) setVisit(null); }
};

export const isOffline = (e) => offlineNow() || isNetErr(e);

const form = (o) => { const f = new FormData(); Object.entries(o).forEach(([k, v]) => v !== undefined && f.append(k, v)); return f; };

// Uploads a worker document now, or keeps it on the phone when there is no signal. Returns "sent" or "queued".
export const sendDoc = async (api, worker, docType, file, what) => {
  const data = { worker_id: worker.id, doc_type: docType, file };
  const keep = async () => { await enqueue("doc", `${what} · ${worker.worker_full_name}`, data); return "queued"; };
  if (offlineNow()) return keep();
  try { await api.post(`/workers/${worker.id}/documents`, form(data)); return "sent"; } catch (e) { if (isOffline(e)) return keep(); throw e; }
};
export const QUEUED_MSG = "No signal. Saved on this phone; it uploads automatically when you're back online.";

// Attendance clashes: marks someone made online for the same day after this phone saved its copy. Online marks are kept.
const CLASH_KEY = "c369-att-clash";
export const listClashes = () => JSON.parse(localStorage.getItem(CLASH_KEY) || "[]");
export const dropClash = (c) => { localStorage.setItem(CLASH_KEY, JSON.stringify(listClashes().filter((x) => !(x.date === c.date && x.worker_id === c.worker_id)))); ping(CLASH_EVENT); };
const addClashes = (list) => {
  const keep = listClashes().filter((x) => !list.some((c) => c.date === x.date && c.worker_id === x.worker_id));
  localStorage.setItem(CLASH_KEY, JSON.stringify([...keep, ...list])); ping(CLASH_EVENT);
};

// Saves attendance now, or keeps it on the phone. base = the marks this phone saw when the day was loaded.
export const saveAttendance = async (api, path, rows, day, marks) => {
  const entries = Object.entries(marks).map(([worker_id, status]) => ({ worker_id, status }));
  const base = Object.fromEntries((rows || []).map((w) => [w.id, w.status || null]));
  const names = Object.fromEntries((rows || []).map((w) => [w.id, w.worker_full_name]));
  const keep = async () => {
    await enqueue("attendance", `Attendance ${day} · ${entries.filter((e) => e.status !== base[e.worker_id]).length} change(s)`, { date: day, entries, base, names });
    cachePut(path, (rows || []).map((w) => ({ ...w, status: marks[w.id] || w.status })));
    return { queued: true };
  };
  if (offlineNow()) return keep();
  try { return (await api.post("/attendance", { date: day, entries })).data; } catch (e) { if (isOffline(e)) return keep(); throw e; }
};

const RUN = {
  doc: (api, d) => api.post(`/workers/${d.worker_id}/documents`, form({ doc_type: d.doc_type, file: d.file })),
  // Each file is uploaded once; progress is saved so a retry only re-sends the worker details.
  worker: async (api, d, it) => {
    const body = d.body;
    body.upload_ids = body.upload_ids || [];
    for (const x of d.files) {
      if (x.done) continue;
      const r = (await api.post(x.side ? "/workers/ocr-aadhaar" : "/kyc/read", form({ side: x.side, kind: x.kind, file: x.file }))).data;
      body.upload_ids.push(r.upload_id);
      Object.entries(x.map || {}).forEach(([src, dst]) => { if (!body[dst] && r.fields?.[src]?.confidence >= 80) body[dst] = r.fields[src].value; });
      x.done = true; x.file = null;
      await put(it);
    }
    await api.post("/workers", body);
  },
  attendance: async (api, d) => {
    const cur = Object.fromEntries((await api.get(`/attendance?day=${d.date}`)).data.map((w) => [w.id, w]));
    const send = [], clash = [];
    for (const e of d.entries) {
      const s = cur[e.worker_id]?.status || null, b = d.base[e.worker_id] ?? null;
      if (e.status === b) continue;
      if (s && s !== b && s !== e.status) clash.push({ date: d.date, worker_id: e.worker_id, name: cur[e.worker_id]?.worker_full_name || d.names?.[e.worker_id], online: s, mine: e.status });
      else if (s !== e.status) send.push(e);
    }
    if (send.length) await api.post("/attendance", { date: d.date, entries: send });
    if (clash.length) addClashes(clash);
    return clash.length;
  },
};

let running = false;
// Sends saved items one by one; failed ones (not network) are kept with the error and only retried on "Upload now".
export const syncQueue = async (api, manual) => {
  const out = { ok: 0, failed: 0, labels: [], clashes: 0 };
  if (running || offlineNow()) return out;
  running = true;
  try {
    for (const it of await listQueue()) {
      if (it.error && !manual) continue;
      try {
        const r = await RUN[it.kind](api, it.data, it);
        if (it.kind === "attendance") out.clashes += r;
        await tx("queue", "readwrite", (s) => s.delete(it.id)); out.ok++; out.labels.push(it.label);
      } catch (e) {
        if (isOffline(e)) break;
        out.failed++; await put({ ...it, error: errMsg(e) });
      }
    }
    if (!offlineNow()) await closeVisit(api);
  } finally { running = false; ping(); if (out.ok) ping(SYNCED); }
  return out;
};

const D = [[0,1,2,3,4,5,6,7,8,9],[1,2,3,4,0,6,7,8,9,5],[2,3,4,0,1,7,8,9,5,6],[3,4,0,1,2,8,9,5,6,7],[4,0,1,2,3,9,5,6,7,8],[5,9,8,7,6,0,4,3,2,1],[6,5,9,8,7,1,0,4,3,2],[7,6,5,9,8,2,1,0,4,3],[8,7,6,5,9,3,2,1,0,4],[9,8,7,6,5,4,3,2,1,0]];
const P = [[0,1,2,3,4,5,6,7,8,9],[1,5,7,6,2,8,3,0,9,4],[5,8,0,3,7,9,6,1,4,2],[8,9,1,6,0,4,3,5,2,7],[9,4,5,3,1,2,6,8,7,0],[4,2,8,6,5,7,3,9,0,1],[2,7,9,3,8,0,6,4,1,5],[7,0,4,6,9,1,3,2,5,8]];
export const verhoeffOk = (n) => /^[2-9]\d{11}$/.test(n) && [...n].reverse().reduce((c, ch, i) => D[c][P[i % 8][+ch]], 0) === 0;
